import React from 'react';
import styled from 'styled-components';
import { FormattedMessage, useIntl } from 'react-intl';

import { Locales, messageToString, PrimaryAnchor } from '@ratehub/base-ui';

import Paths from '../definitions/Paths';
import HeroTabContentListItemContainer from './HeroTabContentListItemContainer';


function HeroTabContentInvesting() {
    const intl = useIntl();

    const ctaLink = intl.locale === Locales.FRENCH
        ? (Paths.BEST_GICS_FR ?? Paths.BEST_GICS_EN)
        : Paths.BEST_GICS_EN;

    return (
        <Container className="rh-display-grid rh-gap-1">
            {/* Column 1 */}
            <div className="rh-box-shadow-surround rh-p-1 rh-border-radius-3px rh-display-flex rh-flex-direction-column rh-gap-1">
                <h2 className="rh-fg-blueberry-dark weight-medium rh-m-0">
                    <FormattedMessage
                        id="web-components.HeroTabContentInvesting.col1.title"
                        defaultMessage="Compare investment options"
                    />
                </h2>
                <HeroTabContentListItemContainer
                    listItems={listItems}
                />
            </div>

            {/* Column 2 */}
            <div className="col-2 rh-box-shadow-surround rh-p-1 rh-border-radius-3px rh-text-align-center rh-display-flex rh-flex-direction-column rh-justify-content-space-between rh-gap-1">
                <div className="image-wrapper rh-position-relative rh-width-100p rh-height-100p">
                    <img
                        // Placeholder image until we have a proper design
                        src="https://media.istockphoto.com/id/2215035178/photo/smile-business-and-people-on-tablet-in-office-of-planning-finance-document-and-company-growth.jpg?s=2048x2048&w=is&k=20&c=4EE-OPo9SVT0RI4Ee9SNtZ17guoDqeQ2NrHPH0WdMIQ%3D"
                        alt={messageToString(
                            <FormattedMessage
                                id="web-components.HeroTabContentInvesting.img.alt"
                                defaultMessage="Smiling woman sits with businessman in brightly lit room reviewing tablet"
                            />,
                            intl,
                        )}
                        className="rh-position-absolute"
                    />
                </div>
                <div className="cta-section">
                    <h2 className="rh-fg-blueberry-dark weight-medium rh-m-0">
                        <FormattedMessage
                            id="web-components.HeroTabContentInvesting.col2.title"
                            defaultMessage="Guaranteed investment certificates"
                        />
                    </h2>
                    <p className="rh-m-0">
                        <FormattedMessage
                            id="web-components.HeroTabContentInvesting.col2.subtitle"
                            defaultMessage="Find competitive rates and lock in returns"
                        />
                    </p>
                    <PrimaryAnchor
                        href={ctaLink}
                        size="medium"
                        className="rh-mt-1"
                        data-name="hero-tab-content-gic-compare-link"
                    >
                        <FormattedMessage
                            id="web-components.HeroTabContentInvesting.col2.button"
                            defaultMessage="Compare now"
                        />
                    </PrimaryAnchor>
                </div>
            </div>
        </Container>
    );
}

const Container = styled.div`
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    grid-template-rows: auto;

    > .col-2 {
        > .image-wrapper {
            min-height: 15rem;

            > img {
                max-width: 100%;
                max-height: 100%;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                object-fit: cover;
                border-radius: 100vh;
                aspect-ratio: 1/1;
            }
        }

        > .cta-section {
            flex: 1 1 auto;
        }
    }
`;

const listItems = [
    {
        iconKey: 'headset',
        title: (
            <FormattedMessage
                id="web-components.HeroTabContentInvesting.listItems.robo-advisors.title"
                defaultMessage="Robo advisors"
            />
        ),
        subtitle: (
            <FormattedMessage
                id="web-components.HeroTabContentInvesting.listItems.robo-advisors.subtitle"
                defaultMessage="Build wealth with automated investing"
            />
        ),
        href: {
            [Locales.ENGLISH]: Paths.ROBO_ADVISORS_EN,
            [Locales.FRENCH]: Paths.ROBO_ADVISORS_FR,
        },
    },
    {
        iconKey: 'brokerage',
        title: (
            <FormattedMessage
                id="web-components.HeroTabContentInvesting.listItems.online-brokerages.title"
                defaultMessage="Online brokerages"
            />
        ),
        subtitle: (
            <FormattedMessage
                id="web-components.HeroTabContentInvesting.listItems.online-brokerages.subtitle"
                defaultMessage="Grow your portfolio with online trading"
            />
        ),
        href: {
            [Locales.ENGLISH]: Paths.ONLINE_BROKERS_EN,
            [Locales.FRENCH]: Paths.ONLINE_BROKERS_FR,
        },
    },
    {
        iconKey: 'crypto',
        title: (
            <FormattedMessage
                id="web-components.HeroTabContentInvesting.listItems.cryptocurrency-exchanges.title"
                defaultMessage="Cryptocurrency exchanges"
            />
        ),
        subtitle: (
            <FormattedMessage
                id="web-components.HeroTabContentInvesting.listItems.cryptocurrency-exchanges.subtitle"
                defaultMessage="Diversify with crypto investing"
            />
        ),
        href: {
            [Locales.ENGLISH]: Paths.CRYPTO_EXCHANGES_EN,
            [Locales.FRENCH]: Paths.CRYPTO_EXCHANGES_FR,
        },
    },
];

export default HeroTabContentInvesting;
